import { createBrowserRouter } from 'react-router'
import { lazy, Suspense, type ComponentType } from 'react'
import { AdminAuthProvider } from '@provendoro/dashboard/features/auth/AdminAuthProvider.js'
import { BrandProvider } from '@provendoro/dashboard/features/brands/BrandProvider.js'
import { AdminProtectedRoute } from '@provendoro/dashboard/features/auth/AdminProtectedRoute.js'
import { AdminLayout } from '@provendoro/dashboard/features/admin/AdminLayout.js'
import { WholesaleProtectedRoute } from './features/wholesale/WholesaleProtectedRoute.js'

/**
 * Wraps lazy() to handle stale chunk errors after deployment.
 * When a dynamic import fails (old chunk hash no longer on server),
 * reload the page once to get fresh HTML with current chunk references.
 */
function lazyRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const key = 'chunk-reload-' + factory.toString().slice(0, 60)
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1')
        window.location.reload()
        return new Promise(() => {}) as never
      }
      sessionStorage.removeItem(key)
      throw err
    }),
  )
}

const LandingPage = lazyRetry(() => import('./features/landing/LandingPage.js'))
const TenantGate = lazyRetry(() => import('./features/storefront/TenantGate.js'))
const HomePage = lazyRetry(() => import('./features/storefront/HomePage.js'))

/**
 * Returns true when the app is running on a tenant subdomain
 * (e.g. lukes-lemons.provendoro.com) rather than the main marketing domain.
 */
function isTenantSubdomain(): boolean {
  const { hostname } = window.location
  // Match *.provendoro.com but not www or the root domain itself
  return (
    hostname.endsWith('.provendoro.com') &&
    hostname !== 'www.provendoro.com' &&
    hostname.split('.').length === 3
  )
}
const ProductListPage = lazyRetry(() => import('./features/storefront/ProductListPage.js'))
const ProductDetailPage = lazyRetry(() => import('./features/storefront/ProductDetailPage.js'))
const SearchResults = lazyRetry(() => import('./features/storefront/SearchResults.js'))
const LoginPage = lazyRetry(() => import('./features/auth/LoginPage.js'))
const RegisterPage = lazyRetry(() => import('./features/auth/RegisterPage.js'))
const ForgotPasswordPage = lazyRetry(() => import('./features/auth/ForgotPasswordPage.js'))
const RecipeListPage = lazyRetry(() => import('./features/recipes/RecipeListPage.js'))
const RecipeDetailPage = lazyRetry(() => import('./features/recipes/RecipeDetailPage.js'))
const CartPage = lazyRetry(() => import('./features/cart/CartPage.js'))
const CheckoutPage = lazyRetry(() => import('./features/checkout/CheckoutPage.js'))
const OrderConfirmation = lazyRetry(() => import('./features/orders/OrderConfirmation.js'))
const OrderHistory = lazyRetry(() => import('./features/orders/OrderHistory.js'))
const OrderDetail = lazyRetry(() => import('./features/orders/OrderDetail.js'))
const CateringPage = lazyRetry(() => import('./features/catering/CateringPage.js'))
const PackageDetail = lazyRetry(() => import('./features/catering/PackageDetail.js'))
const BookingForm = lazyRetry(() => import('./features/catering/BookingForm.js'))
const BookingConfirmation = lazyRetry(() => import('./features/catering/BookingConfirmation.js'))
const QuoteWizard = lazyRetry(() => import('./features/catering/QuoteWizard.js'))
const QuoteConfirmation = lazyRetry(() => import('./features/catering/QuoteConfirmation.js'))
const QuotePortal = lazyRetry(() => import('./features/catering/QuotePortal.js'))
const SignupPage = lazyRetry(() => import('./features/signup/SignupPage.js'))
const OnboardingPage = lazyRetry(() => import('./features/onboarding/OnboardingPage.js'))

// Wholesale portal pages
const WholesaleLayout = lazyRetry(() => import('./features/wholesale/WholesaleLayout.js'))
const WholesaleHome = lazyRetry(() => import('./features/wholesale/WholesaleHome.js'))
const WholesaleCatalog = lazyRetry(() => import('./features/wholesale/WholesaleCatalog.js'))
const OrderGuides = lazyRetry(() => import('./features/wholesale/OrderGuides.js'))
const OrderGuideDetail = lazyRetry(() => import('./features/wholesale/OrderGuideDetail.js'))
const QuickOrder = lazyRetry(() => import('./features/wholesale/QuickOrder.js'))
const WholesaleCart = lazyRetry(() => import('./features/wholesale/WholesaleCart.js'))
const WholesaleOrderHistory = lazyRetry(() => import('./features/wholesale/WholesaleOrderHistory.js'))
const WholesaleOrderDetail = lazyRetry(() => import('./features/wholesale/WholesaleOrderDetail.js'))
const WholesaleInvoices = lazyRetry(() => import('./features/wholesale/WholesaleInvoices.js'))
const WholesaleInvoiceDetail = lazyRetry(() => import('./features/wholesale/WholesaleInvoiceDetail.js'))
const WholesaleAccountPage = lazyRetry(() => import('./features/wholesale/WholesaleAccount.js'))

// Vendor dashboard pages (admin section of the storefront, served at /vendor/*)
const AdminLoginPage = lazyRetry(() => import('@provendoro/dashboard/features/auth/AdminLoginPage.js'))
const DashboardPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/DashboardPage.js'))
const OrderQueuePage = lazyRetry(() => import('@provendoro/dashboard/features/admin/orders/OrderQueuePage.js'))
const OrderDetailPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/orders/OrderDetailPage.js'))
const VendorProductListPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/products/ProductListPage.js'))
const VendorProductCreatePage = lazyRetry(() => import('@provendoro/dashboard/features/admin/products/ProductCreatePage.js'))
const VendorProductEditPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/products/ProductEditPage.js'))
const VendorProductViewPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/products/ProductViewPage.js'))
const CategoryManager = lazyRetry(() => import('@provendoro/dashboard/features/admin/categories/CategoryManager.js'))
const InventoryPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/inventory/InventoryPage.js'))
const VendorRecipeListPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/recipes/RecipeListPage.js'))
const RecipeEditor = lazyRetry(() => import('@provendoro/dashboard/features/admin/recipes/RecipeEditor.js'))
const CateringDashboard = lazyRetry(() => import('@provendoro/dashboard/features/admin/catering/CateringDashboard.js'))
const PackageEditor = lazyRetry(() => import('@provendoro/dashboard/features/admin/catering/PackageEditor.js'))
const BookingManager = lazyRetry(() => import('@provendoro/dashboard/features/admin/catering/BookingManager.js'))
const QuoteRequestsPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/catering/QuoteRequestsPage.js'))
const QuoteReviewPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/catering/QuoteReviewPage.js'))
const SettingsPage = lazyRetry(() => import('@provendoro/dashboard/features/settings/SettingsPage.js'))
const BrandingPage = lazyRetry(() => import('@provendoro/dashboard/features/settings/BrandingPage.js'))
const DomainSettings = lazyRetry(() => import('@provendoro/dashboard/features/settings/DomainSettings.js'))
const BusinessHoursPage = lazyRetry(() => import('@provendoro/dashboard/features/settings/BusinessHoursPage.js'))
const PickupSlotConfig = lazyRetry(() => import('@provendoro/dashboard/features/settings/PickupSlotConfig.js'))
const TeamPage = lazyRetry(() => import('@provendoro/dashboard/features/team/TeamPage.js'))
const WholesaleCustomersPage = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesaleCustomersPage.js'))
const WholesaleInvoicesPage = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesaleInvoicesPage.js'))
const WholesaleOrdersPage = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesaleOrdersPage.js'))
const WholesaleOrderDetailPage = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesaleOrderDetailPage.js'))
const WholesaleDeliveryDashboard = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesaleDeliveryDashboard.js'))
const WholesaleDeliverySchedulePage = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesaleDeliverySchedulePage.js'))
const WholesaleApplicationsPage = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesaleApplicationsPage.js'))
const WholesaleCustomerDetailPage = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesaleCustomerDetailPage.js'))
const WholesaleAgingPage = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesaleAgingPage.js'))
const WholesalePricingPage = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesalePricingPage.js'))
const WholesaleInvoiceDetailPage = lazyRetry(() => import('@provendoro/dashboard/features/wholesale/WholesaleInvoiceDetailPage.js'))
const WholesaleApplyPage = lazyRetry(() => import('./features/wholesale/WholesaleApplyPage.js'))
const AnalyticsPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/analytics/AnalyticsPage.js'))
const SuppliersPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/suppliers/SuppliersPage.js'))
const SupplierDetailPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/suppliers/SupplierDetailPage.js'))
const PurchasingPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/purchasing/PurchasingPage.js'))
const CreatePOPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/purchasing/CreatePOPage.js'))
const PODetailPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/purchasing/PODetailPage.js'))
const PromotionsPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/promotions/PromotionsPage.js'))
const CateringSettingsPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/catering/CateringSettingsPage.js'))
const QuoteTemplatesPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/catering/QuoteTemplatesPage.js'))
const CateringCalendar = lazyRetry(() => import('@provendoro/dashboard/features/admin/catering/CateringCalendar.js'))
const PickListPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/orders/PickListPage.js'))
const LotsPage = lazyRetry(() => import('@provendoro/dashboard/features/admin/inventory/LotsPage.js'))
const WastePage = lazyRetry(() => import('@provendoro/dashboard/features/admin/inventory/WastePage.js'))

const fallback = <div className="p-8 text-center">Loading...</div>

// Routes shared by both storefront (tenant subdomains) and the main site.
// The storefront routes are wrapped in TenantGate on subdomains so that an
// unknown slug shows a "Store not found" page instead of broken loading states.
// Wholesale portal route group - nested under WholesaleProtectedRoute + WholesaleLayout
const wholesaleRoutes = {
  path: '/wholesale',
  element: (
    <WholesaleProtectedRoute>
      <Suspense fallback={fallback}><WholesaleLayout /></Suspense>
    </WholesaleProtectedRoute>
  ),
  children: [
    { index: true, element: <Suspense fallback={fallback}><WholesaleHome /></Suspense> },
    { path: 'catalog', element: <Suspense fallback={fallback}><WholesaleCatalog /></Suspense> },
    { path: 'order-guides', element: <Suspense fallback={fallback}><OrderGuides /></Suspense> },
    { path: 'order-guides/:id', element: <Suspense fallback={fallback}><OrderGuideDetail /></Suspense> },
    { path: 'order/quick', element: <Suspense fallback={fallback}><QuickOrder /></Suspense> },
    { path: 'cart', element: <Suspense fallback={fallback}><WholesaleCart /></Suspense> },
    { path: 'orders', element: <Suspense fallback={fallback}><WholesaleOrderHistory /></Suspense> },
    { path: 'orders/:id', element: <Suspense fallback={fallback}><WholesaleOrderDetail /></Suspense> },
    { path: 'invoices', element: <Suspense fallback={fallback}><WholesaleInvoices /></Suspense> },
    { path: 'invoices/:id', element: <Suspense fallback={fallback}><WholesaleInvoiceDetail /></Suspense> },
    { path: 'account', element: <Suspense fallback={fallback}><WholesaleAccountPage /></Suspense> },
  ],
}

const storefrontRoutes = [
  { path: '/products', element: <Suspense fallback={fallback}><ProductListPage /></Suspense> },
  { path: '/products/:id', element: <Suspense fallback={fallback}><ProductDetailPage /></Suspense> },
  { path: '/search', element: <Suspense fallback={fallback}><SearchResults /></Suspense> },
  { path: '/login', element: <Suspense fallback={fallback}><LoginPage /></Suspense> },
  { path: '/register', element: <Suspense fallback={fallback}><RegisterPage /></Suspense> },
  { path: '/forgot-password', element: <Suspense fallback={fallback}><ForgotPasswordPage /></Suspense> },
  { path: '/recipes', element: <Suspense fallback={fallback}><RecipeListPage /></Suspense> },
  { path: '/recipes/:id', element: <Suspense fallback={fallback}><RecipeDetailPage /></Suspense> },
  { path: '/cart', element: <Suspense fallback={fallback}><CartPage /></Suspense> },
  { path: '/checkout', element: <Suspense fallback={fallback}><CheckoutPage /></Suspense> },
  { path: '/orders/:orderId/confirmation', element: <Suspense fallback={fallback}><OrderConfirmation /></Suspense> },
  { path: '/orders', element: <Suspense fallback={fallback}><OrderHistory /></Suspense> },
  { path: '/orders/:orderId', element: <Suspense fallback={fallback}><OrderDetail /></Suspense> },
  { path: '/catering', element: <Suspense fallback={fallback}><CateringPage /></Suspense> },
  { path: '/catering/:id', element: <Suspense fallback={fallback}><PackageDetail /></Suspense> },
  { path: '/catering/:id/book', element: <Suspense fallback={fallback}><BookingForm /></Suspense> },
  { path: '/catering/booking/:bookingId/confirmation', element: <Suspense fallback={fallback}><BookingConfirmation /></Suspense> },
  { path: '/catering/quote', element: <Suspense fallback={fallback}><QuoteWizard /></Suspense> },
  { path: '/catering/quote/:id/confirmation', element: <Suspense fallback={fallback}><QuoteConfirmation /></Suspense> },
  { path: '/catering/quote/:id/portal', element: <Suspense fallback={fallback}><QuotePortal /></Suspense> },
  wholesaleRoutes,
  { path: '/wholesale/apply', element: <Suspense fallback={fallback}><WholesaleApplyPage /></Suspense> },
]

// Vendor dashboard routes - shared between main domain and tenant subdomain routers
const vendorRoutes = [
  {
    path: '/vendor/login',
    element: (
      <AdminAuthProvider>
        <Suspense fallback={fallback}><AdminLoginPage /></Suspense>
      </AdminAuthProvider>
    ),
  },
  {
    path: '/vendor',
    element: (
      <AdminAuthProvider>
        <BrandProvider>
          <AdminProtectedRoute allowedRoles={['owner', 'manager', 'staff']}>
            <AdminLayout />
          </AdminProtectedRoute>
        </BrandProvider>
      </AdminAuthProvider>
    ),
    children: [
      { path: 'dashboard', element: <Suspense fallback={fallback}><DashboardPage /></Suspense> },
      { path: 'orders', element: <Suspense fallback={fallback}><OrderQueuePage /></Suspense> },
      { path: 'orders/:id', element: <Suspense fallback={fallback}><OrderDetailPage /></Suspense> },
      { path: 'products', element: <Suspense fallback={fallback}><VendorProductListPage /></Suspense> },
      { path: 'products/new', element: <Suspense fallback={fallback}><VendorProductCreatePage /></Suspense> },
      { path: 'products/:id', element: <Suspense fallback={fallback}><VendorProductViewPage /></Suspense> },
      { path: 'products/:id/edit', element: <Suspense fallback={fallback}><VendorProductEditPage /></Suspense> },
      { path: 'categories', element: <Suspense fallback={fallback}><CategoryManager /></Suspense> },
      { path: 'inventory', element: <Suspense fallback={fallback}><InventoryPage /></Suspense> },
      { path: 'recipes', element: <Suspense fallback={fallback}><VendorRecipeListPage /></Suspense> },
      { path: 'recipes/new', element: <Suspense fallback={fallback}><RecipeEditor /></Suspense> },
      { path: 'recipes/:id', element: <Suspense fallback={fallback}><RecipeEditor /></Suspense> },
      { path: 'catering', element: <Suspense fallback={fallback}><CateringDashboard /></Suspense> },
      { path: 'catering/packages/new', element: <Suspense fallback={fallback}><PackageEditor /></Suspense> },
      { path: 'catering/packages/:id', element: <Suspense fallback={fallback}><PackageEditor /></Suspense> },
      { path: 'catering/bookings', element: <Suspense fallback={fallback}><BookingManager /></Suspense> },
      { path: 'catering/bookings/:id', element: <Suspense fallback={fallback}><BookingManager /></Suspense> },
      { path: 'catering/quote-requests', element: <Suspense fallback={fallback}><QuoteRequestsPage /></Suspense> },
      { path: 'catering/quote-requests/:id', element: <Suspense fallback={fallback}><QuoteReviewPage /></Suspense> },
      { path: 'catering/settings', element: <Suspense fallback={fallback}><CateringSettingsPage /></Suspense> },
      { path: 'catering/templates', element: <Suspense fallback={fallback}><QuoteTemplatesPage /></Suspense> },
      { path: 'catering/calendar', element: <Suspense fallback={fallback}><CateringCalendar /></Suspense> },
      { path: 'settings', element: <Suspense fallback={fallback}><SettingsPage /></Suspense> },
      { path: 'settings/branding', element: <Suspense fallback={fallback}><BrandingPage /></Suspense> },
      { path: 'settings/domain', element: <Suspense fallback={fallback}><DomainSettings /></Suspense> },
      { path: 'settings/business-hours', element: <Suspense fallback={fallback}><BusinessHoursPage /></Suspense> },
      { path: 'settings/pickup-slots', element: <Suspense fallback={fallback}><PickupSlotConfig /></Suspense> },
      { path: 'team', element: <Suspense fallback={fallback}><TeamPage /></Suspense> },
      { path: 'wholesale/customers', element: <Suspense fallback={fallback}><WholesaleCustomersPage /></Suspense> },
      { path: 'wholesale/customers/:id', element: <Suspense fallback={fallback}><WholesaleCustomerDetailPage /></Suspense> },
      { path: 'wholesale/invoices', element: <Suspense fallback={fallback}><WholesaleInvoicesPage /></Suspense> },
      { path: 'wholesale/invoices/:id', element: <Suspense fallback={fallback}><WholesaleInvoiceDetailPage /></Suspense> },
      { path: 'wholesale/orders', element: <Suspense fallback={fallback}><WholesaleOrdersPage /></Suspense> },
      { path: 'wholesale/orders/:id', element: <Suspense fallback={fallback}><WholesaleOrderDetailPage /></Suspense> },
      { path: 'wholesale/delivery', element: <Suspense fallback={fallback}><WholesaleDeliveryDashboard /></Suspense> },
      { path: 'wholesale/delivery/:date', element: <Suspense fallback={fallback}><WholesaleDeliveryDashboard /></Suspense> },
      { path: 'wholesale/delivery/schedule', element: <Suspense fallback={fallback}><WholesaleDeliverySchedulePage /></Suspense> },
      { path: 'wholesale/applications', element: <Suspense fallback={fallback}><WholesaleApplicationsPage /></Suspense> },
      { path: 'wholesale/aging', element: <Suspense fallback={fallback}><WholesaleAgingPage /></Suspense> },
      { path: 'wholesale/pricing', element: <Suspense fallback={fallback}><WholesalePricingPage /></Suspense> },
      { path: 'analytics', element: <Suspense fallback={fallback}><AnalyticsPage /></Suspense> },
      { path: 'suppliers', element: <Suspense fallback={fallback}><SuppliersPage /></Suspense> },
      { path: 'suppliers/:id', element: <Suspense fallback={fallback}><SupplierDetailPage /></Suspense> },
      { path: 'purchasing', element: <Suspense fallback={fallback}><PurchasingPage /></Suspense> },
      { path: 'purchasing/new', element: <Suspense fallback={fallback}><CreatePOPage /></Suspense> },
      { path: 'purchasing/:id', element: <Suspense fallback={fallback}><PODetailPage /></Suspense> },
      { path: 'promotions', element: <Suspense fallback={fallback}><PromotionsPage /></Suspense> },
      { path: 'orders/pick-lists', element: <Suspense fallback={fallback}><PickListPage /></Suspense> },
      { path: 'inventory/lots', element: <Suspense fallback={fallback}><LotsPage /></Suspense> },
      { path: 'inventory/waste', element: <Suspense fallback={fallback}><WastePage /></Suspense> },
    ],
  },
]

export const router = isTenantSubdomain()
  ? createBrowserRouter([
      {
        // TenantGate validates the subdomain against the DB before rendering
        // any child route. Unknown subdomains get StoreNotFoundPage.
        element: <Suspense fallback={fallback}><TenantGate /></Suspense>,
        children: [
          { path: '/', element: <Suspense fallback={fallback}><HomePage /></Suspense> },
          ...storefrontRoutes,
        ],
      },
      // Vendor dashboard accessible from tenant subdomains too
      ...vendorRoutes,
    ])
  : createBrowserRouter([
      {
        path: '/',
        element: <Suspense fallback={fallback}><LandingPage /></Suspense>,
      },
      {
        path: '/home',
        element: <Suspense fallback={fallback}><HomePage /></Suspense>,
      },
      ...storefrontRoutes,
      {
        path: '/signup',
        element: <Suspense fallback={fallback}><SignupPage /></Suspense>,
      },
      {
        path: '/onboarding',
        element: <Suspense fallback={fallback}><OnboardingPage /></Suspense>,
      },
      ...vendorRoutes,
    ])
